/*
 * HomeFixer360 - Terms of Service
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 * 
 * This software is proprietary and confidential. Unauthorized copying, distribution,
 * modification, or use is strictly prohibited.
 * 
 * For licensing inquiries: guy@ideaguybrands.com
 */

import React from 'react';
import { ArrowLeft, Scale, Shield, AlertTriangle, FileText, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              onClick={() => window.history.back()}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
              <p className="text-gray-600">Legal agreement for using HomeFixer360</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm border p-8">
          
          {/* Last Updated */}
          <div className="mb-8 p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-900">IMPORTANT LEGAL AGREEMENT</span>
            </div>
            <p className="text-red-700 mt-2">
              <strong>Effective Date:</strong> January 1, 2025<br />
              By using HomeFixer360, you agree to be bound by these terms. Please read carefully.
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Scale className="w-6 h-6 text-blue-600 mr-3" />
              Agreement to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legal agreement between you and Idea Guy Brands Incorporated 
              ("Company," "we," "us," or "our") regarding your use of HomeFixer360, our proprietary satellite roof 
              measurement platform ("Service").
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>By accessing or using our Service, you agree to be bound by these Terms.</strong> If you do not agree 
              to these Terms, you may not use our Service.
            </p>
          </section>

          {/* Service Description */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Service Description</h2>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">HomeFixer360 Platform</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Satellite Measurements:</strong> Proprietary technology for measuring roof dimensions using satellite imagery</li>
              <li><strong>Estimate Generation:</strong> Automated calculation of material and labor costs for home service jobs</li>
              <li><strong>Proposal Creation:</strong> Professional PDF proposals with custom branding and pricing</li>
              <li><strong>Customer Portal:</strong> Secure platform for customers to view and accept proposals</li>
              <li><strong>Project Management:</strong> Basic tools for tracking home service jobs and customer communications</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Service Limitations</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Measurement accuracy depends on satellite image quality and weather conditions</li>
              <li>Service availability is subject to third-party satellite imagery providers</li>
              <li>Estimates are for planning purposes only and do not constitute binding quotes</li>
              <li>Custom pricing settings are your responsibility to maintain and update</li>
            </ul>
          </section>

          {/* Subscription and Billing */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <DollarSign className="w-5 h-5 text-green-600 mr-2" />
              2. Subscription and Billing
            </h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Subscription Plans</h3>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <ul className="space-y-2 text-gray-700">
                <li><strong>Starter:</strong> $299/month + $500 setup fee (50 reports/month)</li>
                <li><strong>Professional:</strong> $499/month + $500 setup fee (200 reports/month)</li>
                <li><strong>Enterprise:</strong> $999/month + $500 setup fee (Unlimited reports)</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Billing Terms</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Payment:</strong> Subscriptions are billed monthly in advance via credit card</li>
              <li><strong>Setup Fee:</strong> One-time $500 setup fee charged upon account activation</li>
              <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless cancelled 48 hours before renewal</li>
              <li><strong>Price Changes:</strong> We may modify pricing with 30 days advance notice</li>
              <li><strong>Taxes:</strong> You are responsible for all applicable taxes and fees</li>
              <li><strong>Late Payments:</strong> Service may be suspended for accounts 7+ days overdue</li>
            </ul>
          </section>

          {/* User Obligations */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. User Obligations and Acceptable Use</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Account Requirements</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>You must be a licensed home service pro or authorized business representative</li>
              <li>You must provide accurate and current registration information</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You must notify us immediately of any unauthorized account access</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Prohibited Uses</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Reverse Engineering:</strong> Attempting to decompile, reverse engineer, or extract our proprietary algorithms</li>
              <li><strong>Unauthorized Access:</strong> Accessing areas of the platform not intended for your use</li>
              <li><strong>Data Mining:</strong> Automated extraction of data beyond normal platform use</li>
              <li><strong>Reselling:</strong> Reselling or redistributing our services without written permission</li>
              <li><strong>Fraudulent Activity:</strong> Using false information or engaging in deceptive practices</li>
              <li><strong>Competitive Intelligence:</strong> Using our service to develop competing products</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Shield className="w-5 h-5 text-purple-600 mr-2" />
              4. Intellectual Property Rights
            </h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Our Proprietary Rights</h3>
            <p className="text-gray-700 mb-4">
              HomeFixer360 and all related technology, including but not limited to software, algorithms, user interfaces, 
              designs, and methodologies, are the exclusive proprietary property of Guy T. Dunn Sr. and Idea Guy Brands Incorporated.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Copyright:</strong> All software code, documentation, and content are protected by copyright</li>
              <li><strong>Trade Secrets:</strong> Measurement algorithms and pricing methodologies are trade secrets</li>
              <li><strong>Trademarks:</strong> HomeFixer360™ and related marks are our registered trademarks</li>
              <li><strong>Patents:</strong> Certain measurement technologies may be protected by pending patent applications</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Your Content and Data</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You retain ownership of your customer data, project information, and business data</li>
              <li>You grant us a license to process your data solely to provide the Service</li>
              <li>You are responsible for ensuring you have rights to all data you upload</li>
              <li>We may use aggregated, anonymized data for service improvement</li>
            </ul>
          </section>

          {/* Service Availability */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Service Availability and Support</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Uptime Goal:</strong> We target 99.9% service availability, excluding scheduled maintenance</li>
              <li><strong>Maintenance:</strong> Scheduled maintenance will be announced 48 hours in advance</li>
              <li><strong>Support:</strong> Customer support is provided via email during business hours</li>
              <li><strong>Emergency Support:</strong> Available for Enterprise customers with dedicated account managers</li>
              <li><strong>Third-Party Dependencies:</strong> Service availability may be affected by satellite imagery providers</li>
            </ul>
          </section>

          {/* Data and Privacy */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Data Protection and Privacy</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Data Security:</strong> We implement industry-standard security measures to protect your data</li>
              <li><strong>Data Backup:</strong> Regular backups ensure data recovery in case of system failure</li>
              <li><strong>Data Export:</strong> You may export your data at any time during your subscription</li>
              <li><strong>Privacy Policy:</strong> Our data practices are governed by our Privacy Policy</li>
              <li><strong>Data Retention:</strong> Data is retained according to our published retention schedule</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Limitation of Liability and Disclaimers</h2>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">IMPORTANT DISCLAIMERS</h3>
              <p className="text-gray-700 text-sm">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Measurement Accuracy</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Satellite measurements are estimates and may vary from actual dimensions</li>
              <li>We do not guarantee specific accuracy percentages for all measurement conditions</li>
              <li>You are responsible for verifying measurements before finalizing customer quotes</li>
              <li>Weather, image quality, and roof complexity may affect measurement accuracy</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Liability Limitations</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Our total liability for any claim shall not exceed the amount you paid in the preceding 12 months</li>
              <li>We are not liable for indirect, incidental, consequential, or punitive damages</li>
              <li>We are not responsible for business losses, lost profits, or project delays</li>
              <li>You agree to indemnify us against claims arising from your use of the Service</li>
            </ul>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Termination</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Your Right to Cancel</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>You may cancel your subscription at any time with 48 hours notice before renewal</li>
              <li>Cancellation takes effect at the end of your current billing period</li>
              <li>No refunds are provided for partial months or setup fees</li>
              <li>You may export your data for 30 days after cancellation</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Our Right to Terminate</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We may suspend or terminate accounts for violation of these Terms</li>
              <li>Immediate termination may occur for fraudulent activity or security breaches</li>
              <li>We may discontinue the Service with 90 days advance notice</li>
              <li>Accounts may be suspended for non-payment after 7 days</li>
            </ul>
          </section>

          {/* Legal Provisions */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Legal and Dispute Resolution</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Governing Law</h3>
            <p className="text-gray-700 mb-4">
              These Terms are governed by the laws of [Your State/Country], without regard to conflict of law principles. 
              Any legal action must be brought in the courts of [Your Jurisdiction].
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Dispute Resolution</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Informal Resolution:</strong> Contact guy@ideaguybrands.com to resolve disputes informally</li>
              <li><strong>Mediation:</strong> Unresolved disputes shall first be subject to mediation</li>
              <li><strong>Arbitration:</strong> Final disputes shall be resolved through binding arbitration</li>
              <li><strong>Class Action Waiver:</strong> You waive the right to participate in class action lawsuits</li>
            </ul>
          </section>

          {/* Miscellaneous */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Miscellaneous Provisions</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Entire Agreement:</strong> These Terms constitute the complete agreement between parties</li>
              <li><strong>Modifications:</strong> Terms may be updated with 30 days advance notice</li>
              <li><strong>Severability:</strong> Invalid provisions do not affect the validity of remaining terms</li>
              <li><strong>Assignment:</strong> You may not assign your rights; we may assign ours</li>
              <li><strong>Force Majeure:</strong> Neither party is liable for delays due to circumstances beyond control</li>
              <li><strong>Survival:</strong> Provisions regarding IP, liability, and disputes survive termination</li>
            </ul>
          </section>

          {/* Contact Information */}
          <section className="mb-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Contact for Legal Matters</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms of Service or legal matters related to HomeFixer360:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Legal Contact:</strong> Guy T. Dunn Sr.</p>
              <p><strong>Company:</strong> Idea Guy Brands Incorporated</p>
              <p><strong>Email:</strong> guy@ideaguybrands.com</p>
              <p><strong>Subject Line:</strong> "Terms of Service - Legal Inquiry"</p>
            </div>
          </section>

          {/* Copyright Footer */}
          <div className="border-t pt-6 mt-8 text-center text-gray-500 text-sm">
            <p>© 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated. All Rights Reserved.</p>
            <p>HomeFixer360™ is a trademark of Idea Guy Brands Incorporated.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;